import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxfWatchlistV1OfacFileQueryServiceOfacFileGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxfWatchlistV1OfacFileState } from '@interxfi/types';
import { OfacFileEventDrawer, OfacFileEventSummary } from '../../../../../../../../../../features/watchlist/ofac/file/ofac-file-event';
import { getOfacFileStateListItems } from '../../../../../../../../../../features/watchlist/ofac/file/ofac-file-list-items';
import { getOfacFileDetailPath } from '../../../../../../../../../../features/watchlist/ofac/file/ofac-file-routing';
import { WatchlistOfacFileStatusPill } from '../../../../../../../../../../features/watchlist/ofac/file/ofac-file-status-pill';
import { OfacSdnsTable } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdns-table';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/ofac/file/_file/$ofacFileId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ofac-file-${params.ofacFileId}`,
        label: 'File',
        to: getOfacFileDetailPath(params.ofacFileId),
        suffix: (
          <CopyButton content={params.ofacFileId} label="Copy OFAC File ID" tooltip={{ copy: params.ofacFileId, copied: 'Copied OFAC File ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('OFAC File'),
  component: OfacFileDetail,
});

function getMenuItems(ofacFileId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: OfacFileEventDrawer,
        props: { request: { ofacFileId } },
      },
    },
    actions: undefined,
  };
}

function getListItems(data: IxfWatchlistV1OfacFileState | undefined) {
  const items = getOfacFileStateListItems(data);

  return {
    left: [items.data.documentUrl, items.data.etag, items.data.publishedDate, items.data.totalRows] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.ofacFileId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load OFAC file', 'view this OFAC file');

function OfacFileDetail() {
  const { ofacFileId } = Route.useParams();
  const request = { ofacFileId } as const;
  const { data, error, isPending } = useIxfWatchlistV1OfacFileQueryServiceOfacFileGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.ofacFile), [data?.ofacFile]);
  const menuItems = useMemo(() => getMenuItems(ofacFileId), [ofacFileId]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <WatchlistOfacFileStatusPill isPending={showPending} size="large" status={data?.ofacFile?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <OfacFileEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>SDNs</Typography.Level3>
        </VerticalStack>

        <OfacSdnsTable ofacFileId={ofacFileId} />
      </PageLayout.Section>
    </PageLayout>
  );
}
